import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { axiosInstance } from "../App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ArrowLeft, CheckCircle, Search } from "lucide-react";
import { toast } from "sonner";

const FollowUpPanel = ({ user }) => {
  const navigate = useNavigate();
  const [regisMembers, setRegisMembers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [nominalDepo, setNominalDepo] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  useEffect(() => {
    fetchRegisMembers();
  }, []);

  const fetchRegisMembers = async () => {
    try {
      const response = await axiosInstance.get("/members?member_type=regis");
      setRegisMembers(response.data);
    } catch (error) {
      toast.error("Gagal memuat data member");
    }
  };

  const handleUpdateToNDP = async (e) => {
    e.preventDefault();
    if (!selectedMember) return;
    
    setLoading(true);
    try {
      await axiosInstance.put(`/members/${selectedMember.id}`, {
        member_type: "ndp",
        nominal_depo: parseFloat(nominalDepo)
      });
      
      toast.success("Member berhasil diupdate ke NDP");
      setShowDialog(false);
      setSelectedMember(null);
      setNominalDepo("");
      fetchRegisMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Gagal mengupdate member");
    } finally {
      setLoading(false);
    }
  };

  const openUpdateDialog = (member) => {
    setSelectedMember(member);
    setNominalDepo("");
    setShowDialog(true);
  };

  const filteredMembers = regisMembers.filter(member =>
    member.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.kode_db.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.phone.includes(searchTerm) ||
    member.marketing_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination logic
  const totalPages = Math.ceil(filteredMembers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedMembers = filteredMembers.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0f0f10] via-[#1a1a1b] to-[#0f0f10]" data-testid="followup-panel">
      <header className="bg-[#1a1a1b]/80 backdrop-blur-xl border-b border-red-900/30 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate("/dashboard")}
              variant="ghost"
              size="sm"
              className="text-gray-300 hover:text-white hover:bg-red-900/20"
              data-testid="back-button"
            >
              <ArrowLeft className="mr-2" size={18} />
              Kembali
            </Button>
            <h1 className="text-2xl font-bold text-red-50">Follow Up Panel</h1>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card className="bg-[#1a1a1b]/80 backdrop-blur-xl border-red-900/30">
          <CardHeader>
            <CardTitle className="text-red-50">Member Regis</CardTitle>
            <CardDescription className="text-gray-400">
              Update member dari Regis ke NDP setelah melakukan deposit
            </CardDescription>
            <div className="mt-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <Input
                  placeholder="Cari member..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-[#0f0f10] border-red-900/30 text-white"
                  data-testid="search-input"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {filteredMembers.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                {searchTerm ? "Tidak ada member yang cocok" : "Tidak ada member Regis saat ini"}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {paginatedMembers.map((member) => (
                  <div
                    key={member.id}
                    className="p-4 bg-[#0f0f10] rounded-lg border border-red-900/20 hover:border-red-800/40 transition-all"
                    data-testid={`regis-member-${member.id}`}
                  >
                    <div className="space-y-2">
                      <div className="flex items-start justify-between">
                        <div className="font-semibold text-white text-lg">{member.username}</div>
                        <span className="px-2 py-1 bg-yellow-900/30 text-yellow-400 text-xs rounded border border-yellow-700/50 font-semibold">
                          REGIS
                        </span>
                      </div>
                      <div className="text-sm text-gray-400">
                        <div>Marketing: {member.marketing_name}</div>
                        <div>Website: {member.website}</div>
                        <div>Kode DB: {member.kode_db}</div>
                        <div>HP: {member.phone}</div>
                        <div className="text-xs mt-2">
                          Dibuat: {new Date(member.created_at).toLocaleDateString('id-ID')}
                        </div>
                      </div>
                      <Button
                        onClick={() => openUpdateDialog(member)}
                        className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white mt-3"
                        data-testid={`update-to-ndp-${member.id}`}
                      >
                        <CheckCircle className="mr-2" size={16} />
                        Update ke NDP
                      </Button>
                    </div>
                  </div>
                  ))}
                </div>
                
                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-6">
                    <Button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      variant="outline"
                      size="sm"
                      className="border-red-900/30 text-white disabled:opacity-50"
                    >
                      Previous
                    </Button>
                    <div className="flex gap-1">
                      {[...Array(totalPages)].map((_, index) => (
                        <Button
                          key={index + 1}
                          onClick={() => handlePageChange(index + 1)}
                          variant={currentPage === index + 1 ? "default" : "outline"}
                          size="sm"
                          className={currentPage === index + 1 
                            ? "bg-red-600 text-white" 
                            : "border-red-900/30 text-white"
                          }
                        >
                          {index + 1}
                        </Button>
                      ))}
                    </div>
                    <Button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      variant="outline"
                      size="sm"
                      className="border-red-900/30 text-white disabled:opacity-50"
                    >
                      Next
                    </Button>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Update Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="bg-[#1a1a1b] border-red-900/30">
            <DialogHeader>
              <DialogTitle className="text-red-50">Update Member ke NDP</DialogTitle>
              <DialogDescription className="text-gray-400">
                Masukkan nominal deposit untuk member {selectedMember?.username}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleUpdateToNDP} className="space-y-4">
              <div className="space-y-2">
                <div className="p-4 bg-[#0f0f10] rounded-lg border border-red-900/20">
                  <div className="text-sm text-gray-400">
                    <div className="text-white font-semibold mb-2">{selectedMember?.username}</div>
                    <div>Marketing: {selectedMember?.marketing_name}</div>
                    <div>Website: {selectedMember?.website}</div>
                    <div>Kode DB: {selectedMember?.kode_db}</div>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="nominal_depo" className="text-gray-300">Nominal Deposit *</Label>
                <Input
                  id="nominal_depo"
                  type="number"
                  step="0.01"
                  value={nominalDepo}
                  onChange={(e) => setNominalDepo(e.target.value)}
                  required
                  placeholder="Masukkan nominal deposit"
                  className="bg-[#0f0f10] border-red-900/30 text-white"
                  data-testid="nominal-depo-input"
                />
              </div>
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowDialog(false)}
                  className="flex-1 border-red-900/30 text-white hover:bg-red-900/20"
                  data-testid="cancel-button"
                >
                  Batal
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800"
                  disabled={loading}
                  data-testid="confirm-update-button"
                >
                  {loading ? "Menyimpan..." : "Update"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default FollowUpPanel;